<template>
  <div class="content">
    <!-- Header: Title + Actions + Navigation -->
    <div class="header p-2 d-flex justify-space-between align-center">
      <div class="title">
        <h3 class="h3 bold mb-n2">{{ projeto.titulo }}</h3>
        <span class="subtitle-2 grey--text text--darken-1">Timesheets</span>
      </div>
      <div class="actions d-flex flex-row align-center">
        <v-btn
          class="elevation-4 mr-2"
          style="font-weight: 500;"
          @click="doLoad"
        >
          <v-icon left dark style="font-size: 20px;">mdi-refresh</v-icon>
          <span style="font-size: 12px">Atualizar</span>
        </v-btn>

        <v-btn
          class="elevation-4 mr-2"
          style="font-weight: 500;"
          @click="doAsyncExport"
        >
          <v-icon left dark style="font-size: 20px;">mdi-cloud-refresh-outline</v-icon>
          <span style="font-size: 12px">Exportação assíncrona</span>
        </v-btn>

        <DropdownCaptacaoV2></DropdownCaptacaoV2>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="d-flex align-center mt-2 mb-2">
      <!-- Filtro de competência -->
      <v-menu
        v-model="dateMenu"
        :close-on-content-click="false"
        transition="scroll-y-transition"
        offset-y
        max-width="290px"
        min-width="290px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="competenciaOrdenada | toMonthPeriod"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
            class="mr-3"
            style="max-width: 250px"
            placeholder="Competência"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="competenciaFiltro"
          range
          type="month"
          @change="onCompetenciaChange"
          no-title
        ></v-date-picker>
      </v-menu>

      <!-- Buscar -->
      <v-text-field
        v-model="search"
        dense
        outlined
        hide-details
        prepend-inner-icon="mdi-magnify"
        placeholder="Buscar..."
        class="mr-3"
        style="max-width: 250px"
      ></v-text-field>

      <v-spacer></v-spacer>

      <!-- Botão Adicionar -->
      <v-btn
        v-if="userHasAccessToAction('create')"
        class="elevation-4 mr-2"
        style="background-color: #1c1c44; color: white;"
        @click="addModalTimesheets = true"
      >
        <v-icon left>mdi-plus-box-outline</v-icon>
        Adicionar timesheet
      </v-btn>

      <!-- Botões de ação em lote (quando houver seleção) -->
      <template v-if="hasSelection">
        <v-btn
          v-if="userHasAccessToAction('update')"
          class="elevation-4 mr-2"
          style="background-color: #1c1c44; color: white;"
          @click="openBulkEdit"
        >
          <v-icon left>mdi-pencil</v-icon>
          Editar ({{ selection.length }})
        </v-btn>
        <v-btn
          v-if="userHasAccessToAction('delete')"
          class="elevation-4"
          color="error"
          @click="openBulkDelete"
        >
          <v-icon left>mdi-delete</v-icon>
          Excluir ({{ selection.length }})
        </v-btn>
      </template>
    </div>

    <!-- Data Table -->
    <div class="body">
      <div class="teros-elevation rounded">
        <v-data-table
          v-model="selection"
          :headers="headers"
          :items="items"
          :loading="loading"
          :search="search"
          item-key="id"
          show-select
          class="mb-7"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100]
          }"
        >
          <!-- Slot para coluna competência -->
          <template v-slot:item.competencia="{ item }">
            {{ item.competencia | toMonth }}
          </template>

          <!-- Slot para coluna valores monetários -->
          <template v-slot:item.totalCustoFunc="{ item }">
            {{ item.totalCustoFunc | toCurrency }}
          </template>
          <template v-slot:item.custoPED="{ item }">
            {{ item.custoPED | toCurrency }}
          </template>
          <template v-slot:item.custoINSS="{ item }">
            {{ item.custoINSS | toCurrency }}
          </template>
          <template v-slot:item.custoFGTS="{ item }">
            {{ item.custoFGTS | toCurrency }}
          </template>

          <!-- Slot para ações na linha -->
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openEditModal(item)" v-if="userHasAccessToAction('update')">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small @click="confirmDelete(item)" v-if="userHasAccessToAction('delete')">
              <v-icon small color="error">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </div>
    </div>

    <!-- AsyncExportButton hidden -->
    <AsyncExportButton
      ref="asyncExportButton"
      :competencia="competenciaOrdenada"
      :projetoId="projetoId"
      class="hidden"
      type="captacao-timesheets"
    />

    <!-- Modal de edição em lote -->
    <FormModal
      :cols="bulkEditFields"
      :opened.sync="updateModalOpened"
      title="Edição em lote"
      :value.sync="updateModalData"
      @save="doMassUpdate"
    />

    <!-- Modal de adicionar timesheet -->
    <FormModal
      :cols="addTimesheetCols"
      :opened.sync="addModalTimesheets"
      :opts="opts"
      title="Adicionar timesheets"
      :value.sync="addModalTimesheetsData"
      @save="doSaveTimesheet"
    />

    <!-- Modal de edição individual -->
    <FormModal
      :cols="editCols"
      :opened.sync="editModalOpened"
      :opts="opts"
      title="Editar timesheet"
      :value.sync="editModalData"
      @save="doUpdateSingle"
    />

    <!-- Modal de confirmação de exclusão -->
    <Modal
      ref="confirmModal"
      appendTitleIcon="mdi-alert-circle-outline"
      title="Exclusão"
      width="400px"
    >
      <p>{{ confirmModalMessage }}</p>

      <template #buttons="{ close }">
        <v-spacer></v-spacer>
        <v-btn color="secondary" dark depressed @click="close(false)">
          Não
        </v-btn>
        <v-btn color="primary" depressed @click="close(true)">
          Sim
        </v-btn>
      </template>
    </Modal>
  </div>
</template>

<script>
import toMonth from "@/helpers/toMonth";
import { mapGetters } from "vuex";
import { toFinite } from "lodash";

export default {
  components: {
    AsyncExportButton: () => import("@/components/async-export-button.vue"),
    DropdownCaptacaoV2: () => import("@/components/dropdown-captacao-v2.vue"),
    FormModal: () => import("@/components/form-modal.vue"),
    Modal: () => import("@/components/g-modal.vue")
  },
  data() {
    return {
      projeto: { titulo: "" },
      items: [],
      selection: [],
      loading: false,
      search: "",
      dateMenu: false,
      competenciaFiltro: [],
      updateModalData: {},
      updateModalOpened: false,
      editModalData: {},
      editModalOpened: false,
      opts: { colaboradores: [] },
      addModalTimesheets: false,
      addModalTimesheetsData: {},
      confirmModalMessage: "",
      pendingDeleteItem: null
    };
  },
  computed: {
    ...mapGetters(["clientId", "intervaloCompetencia"]),
    headers() {
      return [
        { text: "Matrícula", value: "funcionario_matricula", width: "120px" },
        { text: "Colaborador", value: "funcionario_nome" },
        { text: "Competência", value: "competencia", width: "120px" },
        { text: "Dedicação (%)", value: "percentual", width: "120px" },
        { text: "Total Custo Func.", value: "totalCustoFunc", width: "150px" },
        { text: "Custo P&D", value: "custoPED", width: "130px" },
        { text: "Custo INSS", value: "custoINSS", width: "130px" },
        { text: "Encargo INSS (%)", value: "encargoINSS", width: "140px" },
        { text: "Custo FGTS", value: "custoFGTS", width: "130px" },
        { text: "Encargo FGTS (%)", value: "encargoFGTS", width: "140px" },
        { text: "Ações", value: "actions", sortable: false, width: "100px" }
      ];
    },
    hasSelection() {
      return this.selection.length > 1;
    },
    projetoId() {
      return this.$route.params.id;
    },
    bulkEditFields() {
      return [
        {
          key: "none",
          name: `${this.selection.length} registros serão alterados.`,
          type: this.$fieldTypes.SUBTITLE
        },
        {
          key: "dedicacao",
          name: "Dedicação (%)",
          type: this.$fieldTypes.NUMBER,
          rules: [
            { rule: "required" },
            { rule: "between", params: { min: 0, max: 100 } }
          ],
          defaultValue: 0
        }
      ];
    },
    editCols() {
      return [
        {
          key: "id",
          name: "ID",
          type: this.$fieldTypes.TEXT,
          hideInform: true
        },
        {
          key: "funcionario_id",
          name: "Colaborador",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "colaboradores", key: "id", name: "nome" },
          colSize: 12,
          editable: false
        },
        {
          key: "competencia",
          name: "Competência",
          type: this.$fieldTypes.MONTH,
          colSize: 6,
          editable: false
        },
        {
          key: "percentual",
          name: "Dedicação (%)",
          type: this.$fieldTypes.NUMBER,
          rules: [{ rule: "required" }],
          colSize: 6
        }
      ];
    },
    addTimesheetCols() {
      return [
        {
          key: "funcionario_id",
          name: "Colaborador",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rules: [{ rule: "required" }],
          rel: { to: "colaboradores", key: "id", name: "nome" },
          colSize: 12
        },
        {
          key: "competenciaIni",
          name: "Competência início",
          type: this.$fieldTypes.MONTH,
          rules: [{ rule: "required" }],
          colSize: 6
        },
        {
          key: "competenciaFim",
          name: "Competência fim",
          type: this.$fieldTypes.MONTH,
          rules: [{ rule: "required" }],
          colSize: 6
        },
        {
          key: "percentual",
          name: "Dedicação (%)",
          type: this.$fieldTypes.NUMBER,
          rules: [{ rule: "required" }],
          colSize: 12
        }
      ];
    },
    competenciaOrdenada() {
      if (this.competenciaFiltro && this.competenciaFiltro.length === 2) {
        return [...this.competenciaFiltro].sort();
      }
      return this.intervaloCompetencia;
    }
  },
  filters: {
    toMonth(value) {
      return toMonth(value);
    }
  },
  created() {
    this.competenciaFiltro = [...this.intervaloCompetencia];
    this.getProjetoSelecionado();
    this.getColaboradores();
    this.doLoad();
  },
  methods: {
    async doLoad() {
      this.loading = true;
      try {
        const [ini, fim] = this.competenciaOrdenada;
        const query = `competenciaIni=${ini}&competenciaFim=${fim}`;
        const resource = this.apiResource(
          `/v1/captacao/${this.clientId}/timesheets/${this.projetoId}`
        );
        this.items = await resource.get({ query });
      } catch (error) {
        this.notify(error);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    onCompetenciaChange() {
      this.dateMenu = false;
      this.doLoad();
    },
    doAsyncExport() {
      if (this.$refs.asyncExportButton) {
        this.$refs.asyncExportButton.exportCsv();
      }
    },
    async getProjetoSelecionado() {
      try {
        const result = await this.apiResource(
          `/v1/captacao/projetos/${this.clientId}/${this.projetoId}`
        ).get();
        this.projeto = result;
      } catch (error) {
        this.notify(error);
      }
    },
    async getColaboradores() {
      try {
        const result = await this.apiResource(`/v1/rh/${this.clientId}/selecao`).get();
        this.opts.colaboradores = result.colaboradores || [];
      } catch (error) {
        this.notify(error);
      }
    },
    openEditModal(item) {
      this.editModalData = { ...item };
      this.editModalOpened = true;
    },
    async doUpdateSingle(data, closeModal) {
      try {
        const resource = this.apiResource(
          `/v1/captacao/${this.clientId}/timesheets/${this.projetoId}`
        );
        await resource.save({
          id: data.id,
          funcionario_id: data.funcionario_id,
          percentual: data.percentual,
          competencia: data.competencia
        }, data.id);
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Sucesso",
          text: "Registro atualizado com sucesso."
        });
        closeModal();
        this.doLoad();
      } catch (error) {
        this.notify(error);
      }
    },
    openBulkEdit() {
      this.updateModalData = { dedicacao: 0 };
      this.updateModalOpened = true;
    },
    async doMassUpdate(data, closeModal) {
      try {
        const dedicacao = toFinite(data.dedicacao ?? 0);
        const url = `/v1/captacao/${this.clientId}/timesheets/${this.projetoId}/multiple`;
        const timesheetIds = this.selection.map(({ id }) => id);
        await this.api().post(url, { dedicacao, timesheetIds });
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Edição concluída",
          text: `Foram alterados ${timesheetIds.length} registros.`
        });
        closeModal();
        this.selection = [];
        this.doLoad();
      } catch (error) {
        this.notify(error);
      }
    },
    async confirmDelete(item) {
      this.pendingDeleteItem = item;
      const { competencia, funcionario_matricula, funcionario_nome } = item;
      this.confirmModalMessage = `Deseja excluir o registro de ${
        funcionario_matricula ? `${funcionario_matricula} — ` : ""
      }${funcionario_nome} para competência ${toMonth(competencia)}?`;
      
      const response = await this.$refs.confirmModal.asyncOpen();
      if (response) {
        await this.doDeleteSingle(item);
      }
      this.pendingDeleteItem = null;
    },
    async doDeleteSingle(item) {
      try {
        const resource = this.apiResource(
          `/v1/captacao/${this.clientId}/timesheets/${this.projetoId}`
        );
        await resource.delete(item.id);
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Exclusão concluída",
          text: "Registro excluído com sucesso."
        });
        this.doLoad();
      } catch (error) {
        this.notify(error);
      }
    },
    async openBulkDelete() {
      this.confirmModalMessage = `Deseja excluir ${this.selection.length} registros?`;
      const response = await this.$refs.confirmModal.asyncOpen();
      if (response) {
        await this.doBulkDelete();
      }
    },
    async doBulkDelete() {
      try {
        const url = `/v1/captacao/${this.clientId}/timesheets/${this.projetoId}/multiple/delete`;
        const timesheetIds = this.selection.map(({ id }) => id);
        await this.api().delete(url, { data: { timesheetIds } });
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Exclusão concluída",
          text: `Foram excluídos ${timesheetIds.length} registros.`
        });
        this.selection = [];
        this.doLoad();
      } catch (error) {
        this.notify(error);
      }
    },
    async doSaveTimesheet() {
      try {
        const resource = this.apiResource(
          `/v1/captacao/${this.clientId}/timesheets/${this.projetoId}`
        );
        await resource.save(this.addModalTimesheetsData);
        this.$notify({
          type: "success",
          group: "geral",
          duration: 5000,
          title: "Sucesso",
          text: "Os dados foram salvos com sucesso."
        });
        this.addModalTimesheets = false;
        this.addModalTimesheetsData = {};
        this.doLoad();
      } catch (error) {
        this.notify(error);
        this.addModalTimesheets = false;
        this.addModalTimesheetsData = {};
      }
    }
  },
  watch: {
    clientId() {
      this.$router.push({ name: "projetos-captacao" });
    },
    intervaloCompetencia(newVal) {
      this.competenciaFiltro = [...newVal];
      this.doLoad();
    }
  }
};
</script>

<style lang="scss" scoped>
.hidden {
  display: none;
}

.btn-danger {
  background-color: #dc3545 !important;
  border-color: #dc3545 !important;
}
</style>
